// Levels of Granny's attic. The id is fixed (progress is saved under it) – never change it once the level is published.
// Curve: magnet (1–2), timer magnet (3), distance and materials (4–7), chain reactions and mixed tools (8–15).
// Decoys: the plain magnet next to the timer magnet (it never lets go), the timer magnet next to the ruler for wood.
import { defineLevels } from '../../core/registry';
import { on } from '../../core/level-helpers';
import type { LevelData, StaticData, Vec } from '../../core/types';

/** Only x and y of a placement (goal on a shelf) */
const xy = ({ x, y }: Vec): Vec => ({ x, y });

const L: LevelData[] = [];
{ // 1 – Click – the magnet pulls the ball across the table, past the music box (new: magnet)
  const s1 = { x: 520, y: 520, w: 760, h: 24, skin: 'attic_shelf' } satisfies StaticData;
  L.push({
    id: 'click', par: 1, tools: { attic_magnet: 1 }, learn: ['magnet'], intro: 'attic_magnet',
    statics: [s1, { x: 160, y: 470, w: 26, h: 76, skin: 'attic_cardboard' }],
    balls: [on(s1, -230, 15)],
    goal: { type: 'bell', x: 430, y: 488 },
    objs: [{ t: 'duck', x: 520, y: 488 }],
    solution: [{ t: 'attic_magnet', x: 540, y: 476, a: 90 }],
    fails: [[{ t: 'attic_magnet', x: 1000, y: 476, a: 90 }]],
  });
}
{ // 2 – Up the hill – the magnet pulls the ball up the slope
  const s1 = { x: 560, y: 560, w: 620, h: 22, a: -12, skin: 'attic_shelf' } satisfies StaticData;
  L.push({
    id: 'uphill', par: 1, tools: { attic_magnet: 1 }, learn: ['magnet'],
    statics: [s1, { x: 245, y: 600, w: 26, h: 90, skin: 'attic_cardboard' }, { x: 900, y: 610, w: 140, h: 220, skin: 'attic_suitcase' }],
    balls: [on(s1, -270, 15)],
    goal: { type: 'bell', ...xy(on(s1, -150, 15)) },
    objs: [{ t: 'duck', x: 560, y: 420 }],
    solution: [{ t: 'attic_magnet', x: 460, y: 460, a: 45 }],
    fails: [[{ t: 'attic_magnet', x: 160, y: 440, a: 0 }]],
  });
}
{ // 3 – Hold and let go – the timer magnet catches the ball in mid-air and drops it into the tin (new: timer magnet)
  const s1 = { x: 230, y: 300, w: 380, h: 22, a: 14, skin: 'attic_shelf' } satisfies StaticData;
  L.push({
    id: 'hold-and-release', par: 1, tools: { attic_timer: 1 }, learn: ['magnet'], intro: 'attic_timer',
    statics: [s1, { x: 600, y: 770, w: 1200, h: 40, skin: 'attic_floor' }],
    balls: [on(s1, -150, 15)],
    goal: { type: 'bowl', x: 540, y: 750, w: 150 },
    objs: [{ t: 'duck', x: 800, y: 480 }],
    solution: [{ t: 'attic_timer', x: 520, y: 360, a: 0 }],
  });
}
{ // 4 – Far away – the magnet must be close enough; far away it does not pull
  const s1 = { x: 600, y: 520, w: 900, h: 24, a: -4, skin: 'attic_shelf' } satisfies StaticData;
  L.push({
    id: 'far-away', par: 1, tools: { attic_magnet: 1 }, learn: ['magnet', 'distance'],
    statics: [s1, { x: 170, y: 520, w: 26, h: 80, skin: 'attic_cardboard' }],
    balls: [on(s1, -400, 15)],
    goal: { type: 'bell', ...xy(on(s1, -230, 15)) },
    objs: [{ t: 'duck', x: 520, y: 380 }],
    solution: [{ t: 'attic_magnet', x: 360, y: 380, a: 0 }],
    fails: [[{ t: 'attic_magnet', x: 900, y: 440, a: 90 }], [{ t: 'attic_magnet', x: 700, y: 440, a: 90 }]],
  });
}
{ // 5 – Through the book – the magnet pulls through a book
  L.push({
    id: 'through-the-book', par: 1, tools: { attic_magnet: 1 }, learn: ['magnet', 'materials'],
    statics: [{ x: 600, y: 770, w: 1200, h: 40, skin: 'attic_floor' }, { x: 180, y: 680, w: 70, h: 140, skin: 'attic_suitcase' },
      { x: 440, y: 610, w: 18, h: 280, skin: 'attic_book' }, { x: 300, y: 560, w: 300, h: 22, skin: 'attic_shelf' }],
    balls: [{ x: 240, y: 734, r: 15 }],
    goal: { type: 'bell', x: 395, y: 726 },
    objs: [{ t: 'duck', x: 560, y: 600 }],
    solution: [{ t: 'attic_magnet', x: 500, y: 722, a: 90 }],
    fails: [[{ t: 'attic_magnet', x: 800, y: 722, a: 90 }]],
  });
}
{ // 6 – The wooden ball – the magnet does not pull wood; use the ruler (the timer magnet is a decoy)
  const s1 = { x: 230, y: 300, w: 380, h: 22, a: 14, skin: 'attic_shelf' } satisfies StaticData;
  L.push({
    id: 'wooden-ball', par: 1, tools: { attic_timer: 1, plank: 1 }, learn: ['magnet', 'materials'],
    statics: [s1, { x: 600, y: 770, w: 1200, h: 40, skin: 'attic_floor' }],
    balls: [on(s1, -150, 15, { skin: 'wood' })],
    goal: { type: 'bowl', x: 540, y: 750, w: 150 },
    objs: [{ t: 'duck', x: 800, y: 480 }],
    solution: [{ t: 'plank', x: 700, y: 600, a: -60 }],
    fails: [[{ t: 'attic_timer', x: 520, y: 360, a: 0 }]],
  });
}
{ // 7 – Swing – the magnet bends the ball's fall; too close and it gets stuck
  const s1 = { x: 240, y: 250, w: 300, h: 22, a: 6, skin: 'attic_shelf' } satisfies StaticData;
  L.push({
    id: 'swing', par: 1, tools: { attic_magnet: 1 }, learn: ['magnet', 'distance', 'direction'],
    statics: [s1, { x: 600, y: 770, w: 1200, h: 40, skin: 'attic_floor' }, { x: 380, y: 680, w: 90, h: 140, skin: 'attic_cardboard' }],
    balls: [on(s1, -110, 15)],
    goal: { type: 'bowl', x: 790, y: 750, w: 140 },
    objs: [{ t: 'duck', x: 470, y: 420 }],
    solution: [{ t: 'attic_magnet', x: 620, y: 430, a: 90 }],
    fails: [[{ t: 'attic_magnet', x: 520, y: 470, a: 90 }], [{ t: 'attic_magnet', x: 1000, y: 430, a: 90 }]],
  });
}
{ // 8 – The button – the wooden ball cannot be pulled by the magnet; it must press the button that switches off the holder magnet (new: holder magnet)
  const s1 = { x: 200, y: 250, w: 280, h: 22, a: 6, skin: 'attic_shelf' } satisfies StaticData;
  const bd = { x: 880, y: 470, w: 200, h: 14, a: 6, skin: 'attic_book' } satisfies StaticData;
  L.push({
    id: 'button', par: 1, tools: { plank: 2, attic_magnet: 1 }, learn: ['magnet', 'materials', 'chain_reaction'],
    statics: [s1, bd, { x: 600, y: 770, w: 1200, h: 40, skin: 'attic_floor' }, { x: 520, y: 610, w: 40, h: 280, skin: 'attic_cardboard' }],
    balls: [on(bd, -10, 15), on(s1, -100, 15, { skin: 'wood', helper: true })],
    goal: { type: 'bowl', x: 1010, y: 750, w: 150 },
    objs: [{ t: 'attic_holder', x: 870, y: 508, a: 180, id: 'm1' }, { t: 'button', x: 650, y: 736, act: { fire: 'm1' } }, { t: 'duck', x: 250, y: 560 }],
    solution: [{ t: 'plank', x: 400, y: 300, a: 0 }],
    fails: [[{ t: 'attic_magnet', x: 330, y: 380, a: 0 }]],
  });
}
{ // 9 – Onto the suitcase – the timer magnet lifts the ball and drops it on the suitcase (the plain magnet never lets go)
  const s1 = { x: 330, y: 690, w: 300, h: 22, a: 14, skin: 'attic_shelf' } satisfies StaticData;
  L.push({
    id: 'onto-the-suitcase', par: 1, tools: { attic_magnet: 1, attic_timer: 1 }, learn: ['magnet', 'mechanism'],
    statics: [s1, { x: 600, y: 770, w: 1200, h: 40, skin: 'attic_floor' }, { x: 770, y: 722, w: 250, h: 56, skin: 'attic_suitcase' },
      { x: 785, y: 688, w: 225, h: 16, a: 9, skin: 'attic_book' }],
    balls: [on(s1, -110, 15)],
    goal: { type: 'bowl', x: 985, y: 750, w: 150 },
    objs: [{ t: 'duck', x: 420, y: 480 }],
    solution: [{ t: 'attic_timer', x: 700, y: 600, a: 0 }],
    fails: [[{ t: 'attic_magnet', x: 700, y: 600, a: 0 }]],
  });
}
{ // 10 – The stopper – a heavy steel ball blocks the glass ball; the magnet takes the steel ball, not the glass ball
  const s1 = { x: 290, y: 300, w: 500, h: 22, a: 14, skin: 'attic_shelf' } satisfies StaticData;
  const s2 = { x: 760, y: 410, w: 520, h: 22, skin: 'attic_shelf' } satisfies StaticData;
  L.push({
    id: 'stopper', par: 1, tools: { attic_magnet: 1, plank: 1 }, learn: ['magnet', 'materials'],
    statics: [s1, s2, { x: 688, y: 397, w: 14, h: 8, skin: 'attic_shelf' }, { x: 742, y: 397, w: 14, h: 8, skin: 'attic_shelf' }],
    balls: [on(s1, -200, 15, { skin: 'glass' }), { x: 715, y: 372, r: 25, skin: 'steel', helper: true, opts: { density: 0.02 } }],
    goal: { type: 'bell', x: 960, y: 370 },
    objs: [{ t: 'duck', x: 560, y: 470 }],
    solution: [{ t: 'attic_magnet', x: 715, y: 250, a: 0 }],
    fails: [[{ t: 'plank', x: 600, y: 300, a: 0 }]],
  });
}
{ // 11 – The iron – the magnet also pulls iron; the timer magnet fetches the iron and drops it on the seesaw (new: iron)
  const s1 = { x: 170, y: 330, w: 220, h: 22, skin: 'attic_shelf' } satisfies StaticData;
  L.push({
    id: 'iron', par: 1, tools: { attic_timer: 1, attic_magnet: 1, plank: 1 }, learn: ['magnet', 'lever'],
    statics: [s1, { x: 600, y: 770, w: 1200, h: 40, skin: 'attic_floor' }, { x: 500, y: 680, w: 60, h: 140, skin: 'attic_cardboard' }, { x: 622, y: 585, w: 10, h: 40, skin: 'attic_shelf' }],
    fixed: [{ t: 'seesaw', x: 500, y: 600, a: 20 }, { t: 'attic_iron', x: 200, y: 302 }],
    balls: [{ x: 595, y: 585, r: 15, skin: 'glass' }],
    goal: { type: 'bell', x: 610, y: 380 },
    objs: [{ t: 'duck', x: 800, y: 300 }],
    solution: [{ t: 'attic_timer', x: 430, y: 330, a: 90 }],
    fails: [[{ t: 'attic_magnet', x: 430, y: 330, a: 90 }]],
  });
}
{ // 12 – Fish it out – the ball lies in the sewing basket; the timer magnet fishes it out, the ruler leads it to the tin
  const s1 = { x: 190, y: 480, w: 250, h: 22, skin: 'attic_shelf' } satisfies StaticData;
  L.push({
    id: 'fish-it-up', par: 2, tools: { attic_timer: 1, plank: 1, attic_magnet: 1 }, learn: ['magnet', 'ramp'],
    statics: [s1, { x: 600, y: 770, w: 1200, h: 40, skin: 'attic_floor' },
      { x: 80, y: 435, w: 16, h: 70, skin: 'attic_basket' }, { x: 300, y: 445, w: 16, h: 50, skin: 'attic_basket' }],
    balls: [{ x: 190, y: 454, r: 15 }],
    goal: { type: 'bowl', x: 640, y: 750, w: 150 },
    objs: [{ t: 'duck', x: 900, y: 600 }],
    solution: [{ t: 'attic_timer', x: 350, y: 340, a: 45 }, { t: 'plank', x: 430, y: 560, a: 30 }],
    fails: [[{ t: 'attic_magnet', x: 350, y: 340, a: 45 }, { t: 'plank', x: 430, y: 560, a: 30 }]],
  });
}
{ // 13 – The stairs – two timer magnets lift the ball up two suitcases
  const s1 = { x: 240, y: 690, w: 300, h: 22, a: 14, skin: 'attic_shelf' } satisfies StaticData;
  L.push({
    id: 'stairs', par: 2, tools: { attic_timer: 2, attic_magnet: 1, plank: 1 }, learn: ['magnet', 'mechanism'],
    statics: [s1, { x: 600, y: 770, w: 1200, h: 40, skin: 'attic_floor' },
      { x: 580, y: 722, w: 260, h: 56, skin: 'attic_suitcase' }, { x: 580, y: 688, w: 250, h: 16, a: 6, skin: 'attic_book' },
      { x: 820, y: 690, w: 220, h: 120, skin: 'attic_cardboard' }, { x: 820, y: 622, w: 210, h: 16, a: 7, skin: 'attic_book' }],
    balls: [on(s1, -110, 15)],
    goal: { type: 'bowl', x: 1030, y: 750, w: 150 },
    objs: [{ t: 'duck', x: 600, y: 520 }],
    solution: [{ t: 'attic_timer', x: 520, y: 600, a: 0 }, { t: 'attic_timer', x: 760, y: 540, a: 0 }],
    fails: [[{ t: 'attic_timer', x: 520, y: 600, a: 0 }, { t: 'plank', x: 760, y: 600, a: -30 }]],
  });
}
{ // 14 – Tip and click – the sandbag tips the ball up; the magnet catches it at the music box
  L.push({
    id: 'tip-and-click', par: 2, tools: { stone: 1, attic_magnet: 1, plank: 1 }, learn: ['magnet', 'lever'],
    statics: [{ x: 600, y: 770, w: 1200, h: 40, skin: 'attic_floor' }, { x: 500, y: 680, w: 60, h: 140, skin: 'attic_cardboard' }, { x: 622, y: 585, w: 10, h: 40, skin: 'attic_shelf' },
      { x: 790, y: 335, w: 240, h: 22, skin: 'attic_shelf' }],
    fixed: [{ t: 'seesaw', x: 500, y: 600, a: 20 }],
    balls: [{ x: 595, y: 585, r: 15 }],
    goal: { type: 'bell', x: 700, y: 294 },
    objs: [{ t: 'duck', x: 300, y: 300 }],
    solution: [{ t: 'stone', x: 410, y: 300, a: 0 }, { t: 'attic_magnet', x: 720, y: 200, a: 0 }],
    fails: [[{ t: 'stone', x: 410, y: 300, a: 0 }], [{ t: 'attic_magnet', x: 720, y: 200, a: 0 }]],
  });
}
{ // 15 – Master – up over the suitcase tower and down into the biscuit tin: bounce, lift, let go
  const s1 = { x: 180, y: 540, w: 280, h: 22, a: 12, skin: 'attic_shelf' } satisfies StaticData;
  L.push({
    id: 'master', par: 2, tools: { attic_timer: 2, attic_magnet: 1, plank: 2, pad: 1 }, learn: ['magnet', 'mechanism', 'spring'], time: 26,
    statics: [s1, { x: 600, y: 770, w: 1200, h: 40, skin: 'attic_floor' },
      { x: 660, y: 590, w: 120, h: 320, skin: 'attic_suitcase' }, { x: 660, y: 424, w: 120, h: 14, a: 8, skin: 'attic_book' },
      { x: 860, y: 610, w: 260, h: 22, skin: 'attic_shelf' }, { x: 980, y: 550, w: 24, h: 100, skin: 'attic_cardboard' }],
    balls: [on(s1, -110, 15)],
    goal: { type: 'bowl', x: 805, y: 599, w: 150 },
    objs: [{ t: 'duck', x: 540, y: 470 }],
    solution: [{ t: 'pad', x: 460, y: 710, a: 0 }, { t: 'attic_timer', x: 640, y: 330, a: 0 }],
    fails: [[{ t: 'attic_timer', x: 640, y: 330, a: 0 }], [{ t: 'pad', x: 460, y: 710, a: 0 }, { t: 'attic_magnet', x: 640, y: 330, a: 0 }]],
  });
}

export const levels = defineLevels(L);
